import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/Dialog'
import { FacebookPostPreview } from 'modules/creative/previews/FacebookPostPreview'
import { InstagramPostPreview } from 'modules/creative/previews/InstagramPostPreview'
import { PostModalPlatformRow } from 'modules/creative/components/PostModalPlatformRow'
import type { LivePlatform } from 'modules/creative/store/idea-store'
import { EngagementPostDto } from 'openapi'

/**
 * Read-only detail for a LIVE post with no Taka project behind it (published
 * natively on IG/FB). Mirrors PostModal — same dark Dialog shell, the same left
 * platform column (`PostModalPlatformRow`) and the same per-platform preview
 * component PostModal renders. Read-only: a live post can't be edited, and a
 * native post has no Taka project to edit.
 */
export function LivePostModal({
  post,
  onClose,
}: {
  post: EngagementPostDto | null
  onClose: () => void
}) {
  const platform = (post?.platform ?? 'instagram') as LivePlatform

  return (
    <Dialog open={!!post} onOpenChange={open => !open && onClose()}>
      <DialogContent
        variant="dark"
        size="lg"
        className="!flex !h-[85vh] !max-h-[760px] !max-w-[920px] !flex-col !gap-0 !p-0"
      >
        <DialogHeader className="shrink-0 border-b border-line px-4 py-3">
          <DialogTitle variant="dark" className="!text-left !text-sm">
            Post
          </DialogTitle>
        </DialogHeader>

        {post && (
          <div className="flex min-h-0 flex-1 flex-row">
            {/* Left column — platform list, same as PostModal. */}
            <div className="w-[220px] shrink-0 border-r border-line bg-bg-2">
              <div className="px-3 pt-4 pb-2 text-[13px] text-paper-dim">Posted to</div>
              <div className="p-2">
                <PostModalPlatformRow
                  platform={platform}
                  isPreviewing
                  isSelected
                  isConnected
                  isConnecting={false}
                  onPreview={() => {}}
                  onToggleSelect={() => {}}
                  locked
                />
              </div>
            </div>

            {/* Right column — the SAME preview PostModal renders. */}
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {platform === 'facebook' ? (
                <FacebookPostPreview
                  content={{ message: post.caption ?? '', imageUrl: post.imageUrl } as never}
                />
              ) : (
                <InstagramPostPreview
                  content={
                    { caption: post.caption ?? '', hashtags: [], imageUrl: post.imageUrl } as never
                  }
                  postKey={post.postId}
                  compact
                  engagement={{ likeCount: post.likeCount, commentsCount: post.commentsCount }}
                />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
